import type { Signal } from '@preact/signals-react'
import { signal } from '@preact/signals-react'
import type { DeviceName } from '@susu/headless-commerce/components/ResponsiveImage/ResponsiveImage.types'
import {
  IMAGE_CDN_BASE_URL,
  breakpoints,
} from '@susu/headless-commerce/config/config'
import type { Locale } from '@susu/headless-commerce/config/locale'
import type { Step } from '@susu/headless-commerce/features/configurator/domain/Step/Step'
import {
  type LayerImageProps,
  adjustLayeredTransform,
  fetchLayerImageTransformation,
  generateCroppedImagesSrcSets,
} from '@susu/headless-commerce/features/configurator/utils/layerImage'
import { SiteId } from '@susu/headless-commerce/gql/generated/graphql'
import type { CountryConfiguration } from '@susu/headless-commerce/types/CountryConfiguration'
import { isBrowser } from '@susu/headless-commerce/utils/environment'
import log from '@susu/headless-commerce/utils/log'
import type { SignalObject } from '@susu/headless-commerce/utils/signal'
import { isNotNull, isNull } from '@susu/null'
import { isNotUndefined, isUndefined } from '@susu/undefined'
import type { Configurator } from '../Configurator/Configurator'
import type { Selected, Selection } from '../Selection/Selection'
import type { previewCloudinaryConfigurationType } from '../Step/StepDataAccessObject'

export type ImageSourceSets = {
  desktopSrcSet: string
  mobileSrcSet: string
  tabletSrcSet: string
}

export type ImageProductIds = {
  layeredImage: Array<string>
  staticImage: Array<string>
}

export type PreviewData = {
  productIds: string[]
  staticImage: Partial<Record<DeviceName, string | undefined>> | undefined
}

export type LayeredPreviewPosition = {
  zoom: string
  focalPoint: string
}

export class Preview {
  #imageSourcesetSizes = {
    xs: '100vw',
    md: '100vw',
    lg: '70vw',
  }

  imageProductIds: Signal<{
    layeredImage: Signal<Array<string>>
    staticImage: Signal<Array<string>>
  }>
  imageURL: Signal<string>
  imageSourceSets: Signal<{
    desktopSrcSet: Signal<string>
    mobileSrcSet: Signal<string>
    tabletSrcSet: Signal<string>
  }>
  layeredPreviewPosition: Signal<{
    zoom: Signal<string>
    focalPoint: Signal<string>
  }>
  previewData: Signal<{
    productIds: Signal<Array<string>>
    staticImage: Signal<
      Partial<Record<DeviceName, string | undefined>> | undefined
    >
  }>

  /**
   * Parses the given pattern by replacing placeholders with corresponding
   * values from the selection object. If the siteId is 'APAC', special
   * adjustments are made to the selection values.
   *
   * @param pattern - The pattern string containing placeholders in the format {key}.
   * @param selection - An object mapping keys to their respective values.
   * @param siteId - The identifier for the site, which determines specific adjustments.
   * @returns The pattern with placeholders replaced by their corresponding selection values.
   */
  #parseStaticPattern(pattern: string, selection: Selected, siteId: SiteId) {
    // Convert selection object to an array of entries.
    const selectionEntries = Object.entries(selection)
    // Initialize an array to hold modified selection entries.
    const modifiedEntries: [string, string][] = []

    // Iterate over each selection entry.
    for (const [key, value] of selectionEntries) {
      let adjustedValue = value
      if (isNull(value)) {
        continue
      }
      // If siteId is 'APAC' and value ends with 'A', remove the 'A'.
      if (siteId === SiteId.Apac && value.endsWith('A')) {
        adjustedValue = value.slice(0, -1)
      }
      if (isNull(adjustedValue)) {
        continue
      }
      // Add the modified entry to the modifiedEntries array.
      modifiedEntries.push([key, adjustedValue])
    }

    // Create a new object from the modified entries.
    const selectionObject: Record<string, string> = {}
    for (const [key, value] of modifiedEntries) {
      selectionObject[key] = value
    }

    // Replace placeholders in the pattern with values from the selectionObject.
    return pattern.replace(/{([^{}]*)}/g, (_match, key) => selectionObject[key])
  }

  constructor() {
    this.imageProductIds = signal({
      layeredImage: signal([]),
      staticImage: signal([]),
    })
    this.imageURL = signal('')
    this.imageSourceSets = signal({
      desktopSrcSet: signal(''),
      mobileSrcSet: signal(''),
      tabletSrcSet: signal(''),
    })
    this.layeredPreviewPosition = signal({
      focalPoint: signal('0.5 0.5'),
      zoom: signal('100%'),
    })
    this.previewData = signal({
      productIds: signal([]),
      staticImage: signal(undefined),
    })
  }

  updateImageProductIds(steps: Array<Step>) {
    this.imageProductIds.value.layeredImage.value = []
    this.imageProductIds.value.staticImage.value = []

    for (const step of steps) {
      for (const option of step.options.value) {
        if (
          isNotUndefined(option.product.value.id.value) &&
          isNotNull(option.product.value.id.value)
        ) {
          if (step.imageType.value === 'layered-image') {
            this.imageProductIds.value.layeredImage.value.push(
              option.product.value.id.value,
            )
          } else {
            this.imageProductIds.value.staticImage.value.push(
              option.product.value.id.value,
            )
          }
        }
      }
    }
  }

  updatePreviewData(
    configurator: Configurator,
    currentStepData: Step,
    selection: Selected,
    siteId: SiteId,
  ) {
    if (!currentStepData.isLayered()) {
      const { staticImagePatternDesktop, staticImagePatternMobile } =
        currentStepData
      if (
        isUndefined(staticImagePatternDesktop.value) ||
        isUndefined(staticImagePatternMobile.value)
      ) {
        throw new Error('updatePreviewData: No static image pattern')
      }
      this.previewData.value.staticImage.value = {
        mobile: `${IMAGE_CDN_BASE_URL}${this.#parseStaticPattern(staticImagePatternMobile.value, selection, siteId)}`,
        desktop: `${IMAGE_CDN_BASE_URL}${this.#parseStaticPattern(staticImagePatternDesktop.value, selection, siteId)}`,
      }
    } else {
      let filteredSelection: Selected = {
        ...selection,
      }

      // Any option that has a selectionOverride should be applied to the
      // Preview on all steps. Not just the step that it is defined on. For
      // example: The Lapel overrides the Jacket so we want to see the Lapel
      // version of the Jacket on every step.
      filteredSelection =
        configurator.selection.value.getSelectionOverrideSelection(
          configurator,
          filteredSelection,
        )

      // The previewSelectionOverride only counts for the current step. For
      // example: On the Shirt step, we hide certain elements to focus on the
      // shirt.
      filteredSelection =
        configurator.selection.value.getPreviewSelectionOverrideSelection(
          configurator,
          filteredSelection,
        )

      const productIds =
        configurator.selection.value.toProductIds(filteredSelection)

      this.previewData.value.productIds.value = productIds
    }
  }

  updateLayeredPreviewPosition(currentStepData: Step) {
    const { md, lg } = breakpoints
    let device: DeviceName = 'desktop'

    if (isBrowser() && window.matchMedia) {
      if (window.matchMedia(`(max-width: ${md}px)`).matches) {
        device = 'mobile'
      } else if (window.matchMedia(`(max-width: ${lg}px)`).matches) {
        device = 'tablet'
      }
    }

    const zoom = '100%'
    const focalPoint = '0.5 0.5'

    const {
      previewImageFocalPoint,
      previewImageFocalPointMobile,
      previewImageFocalPointTablet,
      previewImageZoom,
      previewImageZoomMobile,
      previewImageZoomTablet,
    } = currentStepData

    const data: Record<DeviceName, SignalObject<LayeredPreviewPosition>> = {
      mobile: {
        focalPoint: signal(previewImageFocalPointMobile.value || focalPoint),
        zoom: signal(previewImageZoomMobile.value || zoom),
      },
      tablet: {
        focalPoint: signal(previewImageFocalPointTablet.value || focalPoint),
        zoom: signal(previewImageZoomTablet.value || zoom),
      },
      desktop: {
        focalPoint: signal(previewImageFocalPoint.value || focalPoint),
        zoom: signal(previewImageZoom.value || zoom),
      },
    }

    this.layeredPreviewPosition.value = data[device]
  }

  async getImageURL(
    props: LayerImageProps,
    categoryStep: string,
    previewCloudinaryConfiguration: previewCloudinaryConfigurationType,
  ) {
    const transformation = await fetchLayerImageTransformation(props)
    const adjusted = adjustLayeredTransform(
      `${IMAGE_CDN_BASE_URL}${transformation}`,
      categoryStep,
      previewCloudinaryConfiguration,
    )

    return adjusted
  }

  async updateImageURL(
    configurator: Configurator,
    modelId: string,
    country: CountryConfiguration,
    locale: Locale,
    categoryStep: string,
    previewCloudinaryConfiguration: previewCloudinaryConfigurationType,
  ) {
    try {
      this.imageURL.value = await this.getImageURL(
        {
          modelId,
          productIds: this.previewData.value.productIds.value,
          siteId: country.siteID,
          locale: locale,
        },
        categoryStep,
        previewCloudinaryConfiguration,
      )
    } catch (error: unknown) {
      const message = (error as Error).message

      const errorMessage = {
        currentSelection: configurator.selection.value.current.value,
        arguments: {
          modelId,
          productIds: this.previewData.value.productIds.value,
          siteId: country.siteID,
          locale: locale,
        },
        categoryStep,
        message,
        previewCloudinaryConfiguration,
      }

      log.error(
        new Error(`Preview.updateImageURL: ${JSON.stringify(errorMessage)}`),
      )

      throw error
    }
  }

  updateImageSourceSets(
    stepName: string,
    previewCloudinaryConfiguration: previewCloudinaryConfigurationType,
  ) {
    const imageSourceSets = generateCroppedImagesSrcSets(
      this.imageURL.value,
      this.#imageSourcesetSizes,
      stepName,
      previewCloudinaryConfiguration,
    )

    this.imageSourceSets.value.desktopSrcSet.value =
      imageSourceSets.desktopSrcSet
    this.imageSourceSets.value.mobileSrcSet.value = imageSourceSets.mobileSrcSet
    this.imageSourceSets.value.tabletSrcSet.value = imageSourceSets.tabletSrcSet
  }

  /**
   * Updates all properties of the Preview.
   */
  async update(
    configurator: Configurator,
    currentStep: Step,
    selection: Selection,
    modelId: string,
    country: CountryConfiguration,
    locale: Locale,
  ) {
    this.updatePreviewData(
      configurator,
      currentStep,
      selection.current.value,
      country.siteID,
    )
    this.updateLayeredPreviewPosition(currentStep)
    if (currentStep.isLayered()) {
      await this.updateImageURL(
        configurator,
        modelId,
        country,
        locale,
        currentStep.name.value,
        currentStep.previewCloudinaryConfiguration.value,
      )
      this.updateImageSourceSets(
        currentStep.name.value,
        currentStep.previewCloudinaryConfiguration.value,
      )
    }
  }
}
